import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.06, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  });
}

const timeline = [
  { year: "1962", title_de: "Der Grundstein in Mölten", title_it: "La fondazione a Mölten", text_de: "Franz Hafner gründet die Baufirma Hafner in Mölten. Der Beginn einer über 60-jährigen Erfolgsgeschichte in Südtirol.", text_it: "Franz Hafner fonda l'impresa edile Hafner a Mölten. L'inizio di una storia di successo lunga oltre 60 anni in Alto Adige." },
  { year: "2011", title_de: "Dritte Generation", title_it: "Terza generazione", text_de: "Jonas Hafner übernimmt und richtet das Unternehmen neu aus: Energie, Nachhaltigkeit, Innovation.", text_it: "Jonas Hafner subentra e riorienta l'azienda: energia, sostenibilità, innovazione." },
  { year: "2013", title_de: "Startschuss Holzbuddy", title_it: "Nascita di Holzbuddy", text_de: "Holzbuddy entsteht als eigenständige Holzbaumarke der Hafner EC GmbH. Seitdem interne Zimmerei, vollständig integriert in die Projektabläufe.", text_it: "Holzbuddy nasce come marchio autonomo di costruzione in legno della Hafner EC GmbH. Da allora, falegnameria interna, completamente integrata nei processi progettuali." },
  { year: "Oggi", title_de: "Premium-Holzbau in Südtirol", title_it: "Costruzione in legno premium in Alto Adige", text_de: "Holzbuddy steht für hochwertige Holzbauprojekte – planbar, präzise, verlässlich.", text_it: "Holzbuddy rappresenta progetti di costruzione in legno di alta qualità – pianificabili, precisi, affidabili." },
];

const team = [
  {
    name: "Philipp",
    rolle_de: "Planung & Projektleitung",
    rolle_it: "Pianificazione & Direzione progetto",
    bild: "https://new.holzbuddy.team/wp-content/uploads/2025/03/Holzbuddy-Team-Hafner-EC-Baufirma-Zimmerer-44-DSCF0700-1229x1536.jpg",
    text_de: "Bauvisionär und Planungsmeister. Philipp denkt Projekte von Anfang bis Ende durch – bevor der erste Nagel gesetzt wird.",
    text_it: "Visionario edile e maestro della pianificazione. Philipp pensa i progetti dall'inizio alla fine – prima che venga posto il primo chiodo."
  },
  {
    name: "Franz",
    rolle_de: "Baustelle & Koordination",
    rolle_it: "Cantiere & Coordinamento",
    bild: "https://new.holzbuddy.team/wp-content/uploads/2025/03/Holzbuddy-Team-Hafner-EC-Baufirma-Zimmerer-63-DSCF0752-819x1024.jpg",
    text_de: "Koordinations-Kapitän und Meister der Baustellenlogistik. Franz sorgt dafür, dass alles kommt, wenn es kommen soll.",
    text_it: "Capitano del coordinamento e maestro della logistica di cantiere. Franz fa sì che tutto arrivi quando deve arrivare."
  },
  {
    name: "Florian",
    rolle_de: "Umsetzung & Montage",
    rolle_it: "Esecuzione & Montaggio",
    bild: "https://new.holzbuddy.team/wp-content/uploads/2025/03/Holzbuddy-Team-Hafner-EC-Baufirma-Zimmerer-32-DSCF0668-768x1024.jpg",
    text_de: "Der Problemlöser. Florian findet auf der Baustelle Lösungen, die im Planungsbüro noch nicht bekannt waren.",
    text_it: "Il risolutore di problemi. Florian trova in cantiere soluzioni che nell'ufficio di progettazione non erano ancora note."
  }
];

export default function UeberUns() {
  const { lang } = useLanguage();
  useReveal();

  return (
    <div className="pt-20 md:pt-24">
      {/* Hero */}
      <section className="section-gap bg-hb-off-white border-b border-hb-border">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-6 reveal">
            <T de="Über Holzbuddy" it="Chi siamo" />
          </span>
          <h1 className="section-title max-w-3xl mb-6 reveal reveal-delay-1" data-testid="ueber-uns-heading">
            <T de="Holzbau ist eine Haltung." it="La costruzione in legno è un atteggiamento." />
          </h1>
          <p className="section-subtitle max-w-xl reveal reveal-delay-2">
            <T de="Holzbuddy ist nicht einfach eine Zimmerei in Südtirol. Es ist eine Entscheidung für Präzision, Verlässlichkeit und handwerkliche Qualität – mit dem Rückhalt eines erfahrenen Bauunternehmens."
               it="Holzbuddy non è semplicemente una falegnameria in Alto Adige. È una scelta per la precisione, l'affidabilità e la qualità artigianale – con il supporto di un'impresa edile esperta." />
          </p>
        </div>
      </section>

      {/* Wer wir sind */}
      <section className="section-gap bg-white" data-testid="ueber-uns-content">
        <div className="container-hb">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="label-text text-hb-wood block mb-5 reveal">
                <T de="Wer wir sind" it="Chi siamo" />
              </span>
              <h2 className="text-3xl sm:text-4xl font-manrope font-extrabold text-hb-dark mb-6 reveal reveal-delay-1">
                <T de="Holzbau mit starker Basis." it="Costruzione in legno con solide fondamenta." />
              </h2>
              <div className="space-y-4 reveal reveal-delay-2">
                <p className="text-base text-hb-text font-inter leading-relaxed">
                  <T de="Holzbuddy ist die Holzbaumarke der Hafner EC GmbH – einem der renommiertesten Bauunternehmen in Südtirol. Seit 2013 bündeln wir Zimmermannshandwerk und Baukompetenz in einer Marke, die für Premium-Holzbau in Meran und Südtirol steht."
                     it="Holzbuddy è il marchio di costruzione in legno della Hafner EC GmbH – una delle imprese edili più rinominate dell'Alto Adige. Dal 2013 uniamo l'artigianato del falegname e la competenza edile in un marchio che rappresenta la costruzione in legno premium a Merano e in Alto Adige." />
                </p>
                <p className="text-base text-hb-text font-inter leading-relaxed">
                  <T de="Was uns von klassischen Zimmereien unterscheidet: Wir denken in Projekten, nicht in Aufgaben. Jedes Holzbauprojekt wird von uns vollständig geplant, strukturiert abgewickelt und konsequent umgesetzt – von der ersten Skizze bis zur Übergabe."
                     it="Ciò che ci distingue dalle falegnamerie classiche: pensiamo in termini di progetti, non di compiti. Ogni progetto di costruzione in legno viene da noi completamente pianificato, gestito in modo strutturato e realizzato con coerenza – dal primo schizzo alla consegna." />
                </p>
                <p className="text-base text-hb-text font-inter leading-relaxed">
                  <T de="Unsere Stärke liegt in der Verbindung: handwerkliche Exzellenz trifft professionelle Projektlogik. Das macht uns zu einem anderen Partner."
                     it="La nostra forza sta nell'unione: l'eccellenza artigianale incontra la logica professionale del progetto. Questo ci rende un partner diverso." />
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src="https://new.holzbuddy.team/wp-content/uploads/2025/03/Holzbuddy-Team-Hafner-EC-Baufirma-Zimmerer-44-DSCF0700-1229x1536.jpg"
                  alt="Holzbuddy Team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="mt-8 aspect-[3/4] overflow-hidden">
                <img
                  src="https://new.holzbuddy.team/wp-content/uploads/2025/03/Holzbuddy-Team-Hafner-EC-Baufirma-Zimmerer-63-DSCF0752-819x1024.jpg"
                  alt="Holzbuddy Team"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-gap bg-hb-off-white" data-testid="team-section">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-5 reveal">
            <T de="Das Team" it="Il Team" />
          </span>
          <h2 className="section-title mb-14 reveal reveal-delay-1">
            <T de="Wer baut." it="Chi costruisce." />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-hb-border">
            {team.map((member, i) => (
              <div
                key={member.name}
                data-testid={`team-member-${member.name.toLowerCase()}`}
                className={`bg-white reveal reveal-delay-${i + 1}`}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={member.bild} alt={member.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-8">
                  <h3 className="text-lg font-manrope font-bold text-hb-dark mb-1">{member.name}</h3>
                  <p className="text-xs tracking-widest uppercase text-hb-wood font-semibold mb-4">
                    {lang === 'it' ? member.rolle_it : member.rolle_de}
                  </p>
                  <p className="text-sm text-hb-text font-inter leading-relaxed">
                    {lang === 'it' ? member.text_it : member.text_de}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-gap bg-white" data-testid="timeline-section">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-5 reveal">
            <T de="Geschichte" it="Storia" />
          </span>
          <h2 className="section-title mb-14 reveal reveal-delay-1">
            <T de="Wie wir geworden sind, was wir sind." it="Come siamo diventati ciò che siamo." />
          </h2>
          <div className="space-y-0">
            {timeline.map((item, i) => (
              <div
                key={item.year}
                className={`grid grid-cols-1 md:grid-cols-4 gap-8 py-10 border-t border-hb-border reveal reveal-delay-${(i % 3) + 1}`}
              >
                <div>
                  <span className="text-3xl font-manrope font-extrabold text-hb-wood">{item.year}</span>
                </div>
                <div className="md:col-span-3">
                  <h3 className="font-manrope font-bold text-hb-dark text-lg mb-2">
                    {lang === 'it' ? item.title_it : item.title_de}
                  </h3>
                  <p className="text-sm text-hb-text font-inter leading-relaxed">
                    {lang === 'it' ? item.text_it : item.text_de}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hafner EC */}
      <section className="section-gap bg-hb-dark" data-testid="hafner-section">
        <div className="container-hb text-center">
          <span className="text-xs tracking-widest uppercase text-white/30 block mb-6">
            <T de="Unsere Mutterfirma" it="La nostra casa madre" />
          </span>
          <h2 className="text-3xl sm:text-4xl font-manrope font-extrabold text-white mb-4">
            <T de="Die Stärke dahinter:" it="La forza alle spalle:" /><br />
            <span className="text-hb-wood">Hafner EC GmbH</span>
          </h2>
          <p className="text-base text-white/60 font-inter max-w-xl mx-auto mb-8">
            <T de="ISO-zertifiziert, KlimaHaus-Partner der ersten Stunde, über 60 Jahre Baugeschichte in Südtirol. Holzbuddy steht auf diesem Fundament."
               it="Certificata ISO, partner KlimaHaus fin dall'inizio, oltre 60 anni di storia edilizia in Alto Adige. Holzbuddy si fonda su queste radici." />
          </p>
          <a
            href="https://www.hafner-ec.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hafner-ec-link"
            className="btn-primary bg-white text-hb-dark hover:bg-hb-off-white"
          >
            <T de="Hafner EC GmbH besuchen" it="Visita Hafner EC GmbH" />
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-hb-off-white border-t border-hb-border">
        <div className="container-hb text-center">
          <h2 className="text-3xl font-manrope font-extrabold text-hb-dark mb-4">
            <T de="Bereit, zusammenzuarbeiten?" it="Pronti a collaborare?" />
          </h2>
          <p className="text-hb-text font-inter mb-8">
            <T de="Wir freuen uns auf Ihr Projekt." it="Siamo lieti di ricevere il vostro progetto." />
          </p>
          <Link
            to="/kontakt"
            data-testid="ueber-uns-cta"
            className="btn-primary"
          >
            <T de="Kontakt aufnehmen" it="Contattaci" />
          </Link>
        </div>
      </section>
    </div>
  );
}
